import { Component } from 'preact'
import axios from 'axios'
import qs from 'qs'

import { ContentType } from '@insided/shared/utils/contentType'

import { ConfigContext, getFormattedContent, getFormattedTitle, showEscalateButton } from '../../utils'
import { AlertMessage, EscalateButton } from '../EscalationButtons'
import CreateEscalationModal from '../CreateEscalationModal'

/* eslint-disable import/no-internal-modules */
import tracker from '../../../../../../components/tracker/publish'
import { publish } from '@insided/deprecated-utils/globals'

interface Props {
  isEnabled: boolean
  title?: string
  username?: string
  userId: string
  content?: string | null
  publicId: number
  moderatorId: string
  isReply: boolean
  contentType: ContentType
  isTrashed: boolean
  destinationUrl: string
  topicId: number
}

interface State {
  showModal: boolean
  ticketUrl: string | null
  isLoading: boolean
  ticketContent: string | null
  ticketSubject?: string
  subjectValidationError?: string
  contentValidationError?: string
  messageType?: string|null
  message?: string
}

class CreateZendeskTicketButton extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      showModal: false,
      ticketUrl: null,
      isLoading: false,
      ticketSubject: getFormattedTitle(this.props.title, this.props.isReply),
      ticketContent: getFormattedContent(this.props.contentType, this.props.destinationUrl, this.props.topicId, this.props.content)
    }
    this.fetchTicketUrl = this.fetchTicketUrl.bind(this)
    this.createZendeskTicket = this.createZendeskTicket.bind(this)
    this.closeModal = this.closeModal.bind(this)
  }

  async componentDidMount () {
    if (this.props.isEnabled) {
      await this.fetchTicketUrl()
    }
  }

  componentDidUpdate (previousProps: Readonly<Props>) {
    if (this.props.isTrashed !== previousProps.isTrashed) {
      this.setState({ messageType: null })
    }

    if (this.props.title !== previousProps.title) {
      this.setState({
        ticketSubject: getFormattedTitle(this.props.title, this.props.isReply)
      })
    }

    if (this.props.content !== previousProps.content) {
      this.setState({
        ticketContent: this.props.content ?? null
      })
    }
  }

  async fetchTicketUrl () {
    this.setState({ isLoading: true })
    try {
      const data = {
        postId: this.props.publicId,
        isReply: this.props.isReply,
        contentType: this.props.contentType,
        type: 'zendesk'
      }
      const result = await axios.get('/escalation/fetch', { params: data })
      const ticketUrl = result.data.ticketUrl
      this.setState({ ticketUrl: ticketUrl, isLoading: false })
      const isReplyToString = this.props.isReply.toString()
      const ticketId = `${this.props.publicId}:${this.props.contentType}:${isReplyToString}`
      publish('zendesk_ticket_created', { url: ticketUrl, id: ticketId })
    } catch (e) {
      this.setState({ ticketUrl: null, isLoading: false })
    }
  }

  async createZendeskTicket () {
    this.setState({ messageType: null, subjectValidationError: undefined, contentValidationError: undefined, isLoading: true })
    const { ticketSubject, ticketContent } = this.state
    const formattedSubject = ticketSubject?.trim()
    const formattedContent = ticketContent?.trim()
    if (formattedSubject === '' && formattedContent === '') {
      this.setState({
        contentValidationError: 'Please fill in the required field.',
        subjectValidationError: 'Please fill in the required field.',
        isLoading: false
      })
      return
    }
    if (formattedContent === '') {
      this.setState({ contentValidationError: 'Please fill in the required field.', isLoading: false })
      return
    }

    if (formattedSubject === '') {
      this.setState({ subjectValidationError: 'Please fill in the required field.', isLoading: false })
      return
    }

    try {
      const { moderatorId, publicId, isReply, username, userId, contentType } = this.props
      const params = {
        moderatorId: moderatorId,
        postId: publicId,
        isReply: isReply,
        contentType: contentType,
        type: 'zendesk',
        title: getFormattedTitle(formattedSubject),
        content: formattedContent,
        authorUsername: username,
        authorUserId: userId
      }
      const result = await axios.post('/escalation/add', qs.stringify(params), { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
      const ticketUrl = result.data.ticketUrl
      this.setState({
        ticketUrl: result.data.ticketUrl,
        showModal: false,
        messageType: 'success',
        message: 'You’ve created a Zendesk ticket.',
        isLoading: false
      })
      const isReplyToString = this.props.isReply.toString()
      const id = `${this.props.publicId}:${this.props.contentType}:${isReplyToString}`
      publish('zendesk_ticket_created', { url: ticketUrl, id: id })
      tracker.trackEvent('userevent', {
        type: 'Post Escalated',
        escalated_to: 'Zendesk',
        id: publicId,
        topic_url: window.location.href,
        topic_content_type: contentType,
        is_opening_post: !isReply
      })
    } catch (e) {
      this.setState({
        ticketUrl: null,
        showModal: false,
        messageType: 'error',
        message: e.response.data.error ?? 'Something went wrong, please try again.',
        isLoading: false
      })
    }
  }

  closeModal () {
    this.setState({
      showModal: false,
      ticketContent: getFormattedContent(this.props.contentType, this.props.destinationUrl, this.props.topicId, this.props.content),
      ticketSubject: getFormattedTitle(this.props.title, this.props.isReply),
      contentValidationError: undefined,
      subjectValidationError: undefined
    })
  }

  render () {
    const { isEnabled, username, isReply, contentType } = this.props
    const { isLoading, showModal, messageType, message, ticketUrl, ticketSubject, ticketContent, subjectValidationError, contentValidationError } = this.state

    if (!isEnabled) {
      return null
    }

    return (
      <ConfigContext.Consumer>
        {({ moderationConfig: { escalate, escalateReply } }) => {
          const showEscalationButton = showEscalateButton(escalate, escalateReply, isReply, Boolean(ticketUrl), contentType)
          return (
            <div className='integration__escalate qa-escalate-zendesk'>
              <AlertMessage messageType={messageType} message={message} />
              {showEscalationButton &&
                <div>
                  <EscalateButton
                    isDisabled={isLoading}
                    tooltipMessage='Create a Zendesk ticket'
                    imageName='zendesk-xs'
                    onClick={() => this.setState({ showModal: true, messageType: null })}
                    customClasses='qa-create-zendesk-ticket'
                  />
                  <CreateEscalationModal
                    modalTitle='Create a Zendesk ticket'
                    modalTitleImage='zendesk'
                    isOpen={showModal}
                    closeModal={() => this.closeModal()}
                    title={ticketSubject}
                    username={username}
                    content={ticketContent}
                    onChangeCallback={(value: string) => this.setState({ ticketSubject: value })}
                    onContentChange={(value: string) => this.setState({ ticketContent: value })}
                    onSubmit={async () => await this.createZendeskTicket()}
                    titleValidationError={subjectValidationError}
                    contentValidationError={contentValidationError}
                    onTextareaFocus={() => this.setState({ contentValidationError: undefined })}
                    onClickCallback={() => this.setState({ subjectValidationError: undefined })}
                    isLoading={isLoading}
                  />
                </div>}
            </div>
          )
        }}
      </ConfigContext.Consumer>
    )
  }
}

export default CreateZendeskTicketButton
